



































































































































import { Component, Prop, Vue } from "vue-property-decorator";

import NoCache from "@/decorators/NoCache";
import VIcon from "@/components/VIcon.vue";
import SettingsListRow from "./SettingsListRow.vue";
import {
  SettingsListAction,
  SettingsListColumn,
  SettingsListItem,
  cellSlotName,
} from "./settingsListTypes";

@Component({ components: { SettingsListRow, VIcon } })
export default class SettingsList extends Vue {
  @Prop({ required: true }) readonly title!: string;
  @Prop({ required: true }) readonly items!: SettingsListItem[];
  @Prop({ required: true }) readonly columns!: SettingsListColumn[];
  @Prop({ default: () => [] }) readonly actions!: SettingsListAction[];
  @Prop({ default: "col-3" }) readonly actionsCol!: string;
  @Prop({ default: null }) readonly rowClass!:
    | ((item: SettingsListItem) => string | string[])
    | null;
  // Rows this predicate rejects cannot be selected, and select-all skips them.
  @Prop({ default: null }) readonly selectableItem!:
    | ((item: SettingsListItem) => boolean)
    | null;

  // List states, owned by the entity list because it owns the request.
  @Prop({ type: Boolean, default: true }) readonly hasProfile!: boolean;
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean;
  @Prop({ type: Boolean, default: false }) readonly error!: boolean;
  @Prop({ type: Boolean, default: false }) readonly deleting!: boolean;

  // Opt-in header buttons.
  @Prop({ type: Boolean, default: false }) readonly refreshable!: boolean;
  @Prop({ type: Boolean, default: false }) readonly selectable!: boolean;
  @Prop({ default: null }) readonly newLabel!: string | null;

  // Expansion. Left off, no chevron is rendered at all.
  @Prop({ type: Boolean, default: false }) readonly expandable!: boolean;
  @Prop({ default: "Mostra dettagli" }) readonly expandTitle!: string;
  @Prop({ default: "Nascondi dettagli" }) readonly collapseTitle!: string;

  // Copy, with generic Italian defaults each list can override.
  @Prop({ default: "Seleziona un profilo per vedere la lista." })
  readonly noProfileText!: string;
  @Prop({ default: "Caricamento…" }) readonly loadingText!: string;
  @Prop({ default: "Errore nel caricamento dei dati." })
  readonly errorText!: string;
  @Prop({ default: "Nessun elemento salvato." }) readonly emptyText!: string;
  @Prop({ default: "azioni" }) readonly actionsLabel!: string;
  @Prop({ default: "Aggiorna" }) readonly refreshLabel!: string;
  @Prop({ default: "Ricarica la lista" }) readonly refreshTitle!: string;
  @Prop({ default: "Seleziona" }) readonly selectLabel!: string;
  @Prop({ default: "Annulla selezione" })
  readonly cancelSelectionLabel!: string;
  @Prop({ default: "Elimina" }) readonly bulkDeleteLabel!: string;
  @Prop({ default: "Eliminazione…" }) readonly bulkDeleteBusyLabel!: string;

  selectionMode = false;
  selectedIds: string[] = [];
  expandedIds: string[] = [];

  // `$scopedSlots` is reassigned before every render and is not reactive, so this
  // getter must not be cached. Only the columns whose `cell-*` slot the parent actually
  // provided are forwarded — forwarding every column would shadow the row's default
  // cell text.
  @NoCache
  get slottedColumns(): SettingsListColumn[] {
    return this.columns.filter(
      (column) => !!this.$scopedSlots[cellSlotName(column.key)]
    );
  }

  // A method, not a computed, so it is re-evaluated on every render.
  slotNameFor(key: string): string {
    return cellSlotName(key);
  }

  rowClassFor(item: SettingsListItem): string | string[] {
    return this.rowClass ? this.rowClass(item) : "";
  }

  // --- Selection -----------------------------------------------------------

  isSelected(item: SettingsListItem): boolean {
    return this.selectedIds.includes(item.id);
  }

  isItemSelectable(item: SettingsListItem): boolean {
    return this.selectableItem ? this.selectableItem(item) : true;
  }

  get selectableItems(): SettingsListItem[] {
    return this.items.filter((item) => this.isItemSelectable(item));
  }

  toggleSelected(item: SettingsListItem) {
    if (!this.isItemSelectable(item)) return;
    this.selectedIds = this.isSelected(item)
      ? this.selectedIds.filter((id) => id !== item.id)
      : [...this.selectedIds, item.id];
    this.emitSelection();
  }

  get allSelected(): boolean {
    const selectable = this.selectableItems;
    return (
      selectable.length > 0 && selectable.every((item) => this.isSelected(item))
    );
  }

  get selectAllIndeterminate(): boolean {
    return this.selectedIds.length > 0 && !this.allSelected;
  }

  toggleSelectAll() {
    this.selectedIds = this.allSelected
      ? []
      : this.selectableItems.map((item) => item.id);
    this.emitSelection();
  }

  toggleSelectionMode() {
    this.selectionMode = !this.selectionMode;
    this.selectedIds = [];
    this.emitSelection();
  }

  // Called by the owning entity list after a successful bulk delete.
  exitSelectionMode() {
    this.selectionMode = false;
    this.selectedIds = [];
    this.emitSelection();
  }

  // Called by the owning entity list when the profile changes.
  reset() {
    this.expandedIds = [];
    this.exitSelectionMode();
  }

  private emitSelection() {
    this.$emit("selection-change", [...this.selectedIds]);
  }

  // --- Expansion (multi-open accordion) ------------------------------------

  isExpanded(item: SettingsListItem): boolean {
    return this.expandedIds.includes(item.id);
  }

  toggleExpand(item: SettingsListItem) {
    this.expandedIds = this.isExpanded(item)
      ? this.expandedIds.filter((id) => id !== item.id)
      : [...this.expandedIds, item.id];
  }
}
